import pipefitterApi from '../../../services/pipefitterApi';
import type { PipeStatusItem } from '../types';
import {
  encodeOverviewGroupIdForQuery,
  type PipeStatusOverviewQuery,
  type PipeStatusOverviewResponse,
  type PipeUploadActivityQuery,
  type PipeUploadActivityResponse,
  type PipeUploadActivityTimeRange,
} from './pipeStatusOverviewTypes';

function buildOverviewParams(query: PipeStatusOverviewQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.projectId != null && query.projectId !== '') {
    params.set('project_id', String(query.projectId));
  }
  const pipeIds = Array.from(
    new Set(
      [
        ...(query.pipeIds ?? []),
        ...(query.pipeId != null && query.pipeId !== '' ? [query.pipeId] : []),
      ]
        .map((id) => String(id).trim())
        .filter(Boolean)
    )
  );
  if (pipeIds.length === 1) {
    params.set('pipe_id', pipeIds[0]);
  } else if (pipeIds.length > 1) {
    params.set('pipeIds', pipeIds.join(','));
  }
  if (query.projectNumber) params.set('project_num', String(query.projectNumber));
  if (query.projectName) params.set('project_name', String(query.projectName));
  params.set('includeArchived', query.includeArchived ? '1' : '0');
  if (query.groupIds && query.groupIds.length > 0) {
    params.set(
      'groupIds',
      query.groupIds.map(encodeOverviewGroupIdForQuery).join(',')
    );
  }
  if (query.endPresence && query.endPresence !== 'all') {
    params.set('endPresence', query.endPresence);
  }
  if (query.flange && query.flange !== 'all') {
    params.set('flange', query.flange);
  }
  return params;
}

function unwrapData<T>(body: any): T {
  if (body?.data != null) return body.data as T;
  return body as T;
}

export async function fetchPipeStatusOverview(
  query: PipeStatusOverviewQuery
): Promise<PipeStatusOverviewResponse> {
  const { data } = await pipefitterApi.get(
    `/reports/pipe-status-overview?${buildOverviewParams(query).toString()}`
  );
  if (data?.status && data.status !== 200) {
    throw new Error(data.message || 'Failed to load pipe status overview');
  }
  const payload = unwrapData<PipeStatusOverviewResponse>(data);
  return {
    projectNumber: payload?.projectNumber ?? null,
    pipeId: payload?.pipeId ?? null,
    summary: payload?.summary ?? {
      groupId: '',
      totalPipes: 0,
      groupCount: 0,
      both: 0,
      endAOnly: 0,
      endBOnly: 0,
    },
    groups: Array.isArray(payload?.groups) ? payload.groups : [],
    availableGroupIds: Array.isArray(payload?.availableGroupIds)
      ? payload.availableGroupIds
      : [],
  };
}

export async function fetchPipeStatusByGroup(
  query: PipeStatusOverviewQuery
): Promise<PipeStatusItem[]> {
  const { data } = await pipefitterApi.get(
    `/reports/pipe-status-by-group?${buildOverviewParams(query).toString()}`
  );
  if (data?.status && data.status !== 200) {
    throw new Error(data.message || 'Failed to load pipes by group');
  }
  const payload = unwrapData<unknown>(data);
  if (Array.isArray(payload)) return payload as PipeStatusItem[];
  if (payload && typeof payload === 'object' && Array.isArray((payload as any).rows)) {
    return (payload as any).rows as PipeStatusItem[];
  }
  return [];
}

function buildUploadActivityParams(query: PipeUploadActivityQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.projectId != null && query.projectId !== '') {
    params.set('project_id', String(query.projectId));
  }
  if (query.projectNumber) params.set('project_num', String(query.projectNumber));
  if (query.projectName) params.set('project_name', String(query.projectName));
  const pipeIds = Array.from(
    new Set(
      (query.pipeIds ?? [])
        .map((id) => String(id).trim())
        .filter(Boolean)
    )
  );
  if (pipeIds.length > 0) {
    params.set('pipeIds', pipeIds.join(','));
  }
  const timeRange: PipeUploadActivityTimeRange = query.timeRange ?? 'all';
  params.set('timeRange', timeRange);
  return params;
}

export async function fetchPipeUploadActivity(
  query: PipeUploadActivityQuery
): Promise<PipeUploadActivityResponse> {
  const { data } = await pipefitterApi.get(
    `/reports/pipe-upload-activity?${buildUploadActivityParams(query).toString()}`
  );
  if (data?.status && data.status !== 200) {
    throw new Error(data.message || 'Failed to load pipe upload activity');
  }
  const payload = unwrapData<PipeUploadActivityResponse>(data);
  return {
    timeRange: payload?.timeRange ?? query.timeRange ?? 'all',
    timezone: payload?.timezone ?? 'UTC',
    kpis: payload?.kpis ?? {
      uploadsInPeriod: 0,
      firstIngests: 0,
      rescans: 0,
      activeDays: 0,
      duplicatePipeEnds: 0,
      extraVersions: 0,
    },
    daily: Array.isArray(payload?.daily) ? payload.daily : [],
    byGroup: Array.isArray(payload?.byGroup) ? payload.byGroup : [],
  };
}
